import { createCryptoCurrency } from '@0xtorch/core'
import { type Client, createClient } from '../../client'
import { type Explorer, createBlockscout } from '../../explorer'
import type { LowerHex } from '../../types'
import type { Chain } from '../types/chain'
import type { HttpRpc } from '../types/rpc'
import { gateLayer } from '../viemChains/gateLayer'

/** GT の価格取得に用いる通貨定義。 */
const gateToken = createCryptoCurrency({
  type: 'CryptoCurrency',
  id: 'gatechain-token',
  name: 'GateToken',
  symbol: 'GT',
  market: { coingeckoId: 'gatechain-token' },
  updatedAt: 0,
})

/**
 * GateLayer mainnet を作成する。
 * @param params.explorerProxyUrl - Explorer API のプロキシ URL。
 * @returns RPC と Blockscout を接続した chain 定義。
 */
export const createGateLayerChain = ({
  explorerProxyUrl: proxyUrl,
}: { explorerProxyUrl?: string }): Chain =>
  createGateLayerChainCustom({
    client: createClient({ chain: gateLayer, httpRpcs: gateLayerHttpRpcs }),
    explorer: createBlockscout({
      name: 'GateScan',
      baseUrl: 'https://www.gatescan.org/gatelayer',
      apiBaseUrl: 'https://www.gatescan.org/gatelayer/api',
      proxyUrl,
    }),
  })

/**
 * GateLayer の依存を指定して chain 定義を作成する。
 * @param params.client - RPC client。
 * @param params.explorer - Explorer 実装。
 * @returns GateLayer の chain 定義。
 */
export const createGateLayerChainCustom = ({
  client,
  explorer,
}: { client: Client; explorer: Explorer }): Chain => ({
  id: 10088,
  network: 'gatelayer',
  name: 'GateLayer',
  nativeToken: {
    name: 'GateToken',
    symbol: 'GT',
    decimals: 18,
    currency: gateToken,
  },
  wrappedTokenAddresses: new Set<LowerHex>([
    '0x6803b8e93b13941f6b73b82e324b80251b3de338',
  ]),
  explorer,
  client,
  blockTime: 1000,
  coingeckoId: undefined,
  defillamaId: undefined,
})

/** 公式 mainnet RPC。ログ取得は確認済みの 1000 block 単位に制限する。 */
export const gateLayerHttpRpcs: HttpRpc[] = [
  {
    url: 'https://gatelayer-mainnet.gatenode.cc',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 1_000n,
  },
]

/** GateLayer の公開 WebSocket RPC 一覧。 */
export const gateLayerWebsocketRpcUrls: readonly string[] = [
  'wss://gatelayer-ws-mainnet.gatenode.cc',
]
